import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import { Form, Field } from 'mobx-react-form';

import { useRootStore } from 'stores/RootStore';

import { withMicrofrontend } from 'utils/microfrontend/useMicrofrontend';
import { CompPlexResidentialLeaseEvents } from '../constants';
import { mapCompPlexResidentialLeaseToRentComp } from './mappers';
import { RentCompsTable } from './RentCompsTable';
import { ResidentialRentComp, ResidentialRentRollUnit } from 'types/underwriting/income-approach';
import { checkIfResidentialRentCompsAreLatestVersion } from './helpers';
import { getSubjectUnitGroups } from '../helpers';

type ResidentialRentCompsProps = {
  form: Form;
  compplexLoaded: boolean;
};

export const groupCompsByBedrooms = (rentComps: ResidentialRentComp[]) => {
  const groups: { [key in number]: { bedrooms: number; comps: ResidentialRentComp[] } } = {};
  rentComps.forEach((comp) => {
    groups[comp.bedrooms] = groups[comp.bedrooms]
      ? { bedrooms: groups[comp.bedrooms].bedrooms, comps: [...groups[comp.bedrooms].comps, comp] }
      : { bedrooms: comp.bedrooms, comps: [comp] };
  });
  return Object.values(groups).sort((previousGroup, nextGroup) => previousGroup.bedrooms - nextGroup.bedrooms);
};

const ResidentialRentComps: React.FC<ResidentialRentCompsProps> = ({ form, compplexLoaded }) => {
  const { underwritingPageStore, authStore } = useRootStore();
  const { underwritingData } = underwritingPageStore;
  const rentRoll: ResidentialRentRollUnit[] = useMemo(
    () => underwritingData?.incomeApproach?.residential?.rentRoll || [],
    [underwritingData]
  );
  const { currentUser } = authStore;

  const rentCompsField: Field = form.$('incomeApproach.residential.rentComps.comps');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rentComps: ResidentialRentComp[] = rentCompsField.values() || [];

  const subjectUnitGroups = useMemo(() => {
    return getSubjectUnitGroups(rentRoll || []);
  }, [rentRoll]);

  const [viewModalComp, setViewModalComp] = useState<{
    leaseId?: string | null;
    leaseVersionNumber?: string | null;
  } | null>({
    leaseId: null,
    leaseVersionNumber: null,
  });
  const [viewModalToggle, setViewModalToggle] = useState(false);

  const compplexRef = useRef<CompPlexElement>(null);

  const subjectProps = {
    coordinates: {
      lat: underwritingData?.property.coords?.latitude,
      lng: underwritingData?.property.coords?.longitude,
    },
  };

  const handleUpdateCompToLatestVersion = useCallback(
    (event: any) => {
      const { residentialRentComp } = event.detail;
      const updatedComp = mapCompPlexResidentialLeaseToRentComp(residentialRentComp);
      rentCompsField.each((field: Field) => {
        if (field.value.leaseId === updatedComp.leaseId) {
          field.set(updatedComp);
        }
      });
    },
    [rentCompsField]
  );

  const removeComp = useCallback(
    (removedComp: { leaseId: string }) => {
      let removedCompKey: string | null = null;
      rentCompsField.each((field: Field) => {
        if (field.value.leaseId === removedComp.leaseId) {
          removedCompKey = field.key;
        }
      });
      if (removedCompKey) {
        rentCompsField.del(removedCompKey);
      }
    },
    [rentCompsField]
  );

  const handleRemoveComp = useCallback(
    (event: any) => {
      const { leaseId } = event.detail;
      removeComp({ leaseId });
    },
    [removeComp]
  );

  const handleAddComp = useCallback(
    (event: any) => {
      const { residentialRentComp } = event.detail;
      const mappedResidentialRentComp = mapCompPlexResidentialLeaseToRentComp(residentialRentComp);
      rentCompsField.add([mappedResidentialRentComp]);
    },
    [rentCompsField]
  );

  const showViewModal = useCallback(
    (comp: any) => {
      setViewModalComp(comp);
      setViewModalToggle(true);
    },
    [setViewModalComp, setViewModalToggle]
  );

  const hideViewModal = useCallback(() => {
    setViewModalComp(null);
    setViewModalToggle(false);
  }, [setViewModalComp, setViewModalToggle]);

  const checkIfCompsAreLatestVersion = useCallback(async () => {
    const updatedComps = await checkIfResidentialRentCompsAreLatestVersion(rentComps);
    rentCompsField.set(updatedComps);
  }, [rentComps, rentCompsField]);

  useEffect(() => {
    const handleError = (event: any) => {
      const { error } = event.detail;
      console.error('CompPlex error:', error?.message || error);
    };

    let currentRef: CompPlexElement | null;
    if (compplexRef.current) {
      compplexRef.current.addEventListener(
        CompPlexResidentialLeaseEvents.EDIT_COMP_UPDATED,
        handleUpdateCompToLatestVersion
      );
      compplexRef.current.addEventListener(CompPlexResidentialLeaseEvents.FOUND_COMP_ADDED, handleAddComp);
      compplexRef.current.addEventListener(CompPlexResidentialLeaseEvents.EDIT_COMP_CLOSED, hideViewModal);
      compplexRef.current.addEventListener(CompPlexResidentialLeaseEvents.MAP_COMP_ADDED, handleAddComp);
      compplexRef.current.addEventListener(CompPlexResidentialLeaseEvents.MAP_COMP_REMOVED, handleRemoveComp);
      compplexRef.current.addEventListener(CompPlexResidentialLeaseEvents.ERROR, handleError);

      currentRef = compplexRef.current;
    }

    checkIfCompsAreLatestVersion();

    return () => {
      if (currentRef) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        compplexRef.current?.removeEventListener(
          CompPlexResidentialLeaseEvents.EDIT_COMP_UPDATED,
          handleUpdateCompToLatestVersion
        );
        currentRef.removeEventListener(CompPlexResidentialLeaseEvents.FOUND_COMP_ADDED, handleAddComp);
        currentRef.removeEventListener(CompPlexResidentialLeaseEvents.EDIT_COMP_CLOSED, hideViewModal);
        currentRef.removeEventListener(CompPlexResidentialLeaseEvents.MAP_COMP_ADDED, handleAddComp);
        currentRef.removeEventListener(CompPlexResidentialLeaseEvents.MAP_COMP_REMOVED, handleRemoveComp);
        currentRef.removeEventListener(CompPlexResidentialLeaseEvents.ERROR, handleError);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const compGroups = groupCompsByBedrooms(rentComps);

  const selectedRentCompIds = rentComps.map((comp) => ({
    id: comp.leaseId,
  }));

  const viewModalProps = {
    showEditModal: viewModalToggle,
    leaseId: viewModalComp?.leaseId || null,
    leaseVersionNumber: viewModalComp?.leaseVersionNumber || null,
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">Residential Rent Comps</Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {compplexLoaded ? (
                <comp-plex-residential-lease
                  ref={compplexRef}
                  auth-user={JSON.stringify(currentUser)}
                  client-portal={true}
                  edit-lease-props={JSON.stringify(viewModalProps)}
                  subject={JSON.stringify(subjectProps)}
                  selected-comps={JSON.stringify(selectedRentCompIds)}
                  onError={(event: any) => {
                    console.error('CompPlex component error:', event.detail);
                  }}
                />
              ) : (
                <Typography variant="body1" align="center" py={4}>
                  Loading Comp Database...
                </Typography>
              )}
            </Grid>
            {compGroups.map(({ bedrooms, comps }) => {
              return (
                <Grid item xs={12} key={`rent-comps-group-${bedrooms}`}>
                  <RentCompsTable
                    subjectUnits={subjectUnitGroups.find((group) => group.bedrooms === bedrooms)?.units || []}
                    comps={comps}
                    name={bedrooms === 0 ? 'Studio' : `${bedrooms} Bedroom${bedrooms > 1 ? 's' : ''}`}
                    removeComp={removeComp}
                    showViewModal={showViewModal}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export const ResidentialRentCompsWithCompplex = withMicrofrontend('compplex', 'compplexLoaded')(ResidentialRentComps);
